import type React from 'react'
import { GripVertical } from 'lucide-react'

import { cn } from '@/utils'

/**
 * `default`: the 36px white pill of `CropToolbar`/`NodeEditToolbar`. `compact`: the 32px
 * theme-coloured pill of `TextOnPathToolbar`.
 */
type GripVariant = 'default' | 'compact'

const GRIP_CLASS: Record<GripVariant, string> = {
  default:
    'touch-none flex h-9 w-7 flex-shrink-0 items-center justify-center rounded-lg border-none p-0 text-[#1a1a1a]/50 transition-colors hover:text-[#1a1a1a]',
  compact:
    'touch-none flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border-none bg-transparent p-0 text-foreground/50 transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
}

const GRIP_ICON_CLASS: Record<GripVariant, string> = {
  default: 'h-5 w-5',
  compact: 'h-4 w-4',
}

interface ToolbarGripButtonProps {
  variant?: GripVariant
  'aria-label'?: string
  isDragging: boolean
  onDragStart: (e: React.PointerEvent<HTMLButtonElement>) => void
  onDragMove: (e: React.PointerEvent<HTMLButtonElement>) => void
  onDragEnd: (e: React.PointerEvent<HTMLButtonElement>) => void
}

/** The 6-dot drag handle that moves a floating toolbar (`useDraggableToolbarAnchor`). */
export function ToolbarGripButton({
  variant = 'default',
  'aria-label': ariaLabel,
  isDragging,
  onDragStart,
  onDragMove,
  onDragEnd,
}: ToolbarGripButtonProps) {
  return (
    <button
      onPointerDown={onDragStart}
      onPointerMove={onDragMove}
      onPointerUp={onDragEnd}
      aria-label={ariaLabel}
      className={cn(GRIP_CLASS[variant], isDragging ? 'cursor-grabbing' : 'cursor-grab')}
    >
      <GripVertical className={GRIP_ICON_CLASS[variant]} />
    </button>
  )
}
